import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useService } from '@evertel/di'
import {
    Checkbox, RoomSearchSelect, Row, Col, Button, Text, Icon,
    Modal, ModalHeader, ModalBody, InfoBox, useUI,
    Tooltip, TooltipTrigger, TooltipContent
} from '@evertel/web/ui'
import { DocumentACLsController } from '@evertel/document'
import { APIDataDocumentACL, APIDataRoom } from '@evertel/types'
import { ACLButton } from './ACLButton'
import { ACL } from './ACL'
import { DepartmentController } from '@evertel/department'
import { CurrentUserDepartmentsController } from '@evertel/blue-user'
import { DepartmentAvatar } from '@evertel/web/department'
import { Links } from '@evertel/constants'

interface DocumentShareModalProps {
    isVisible: boolean
    onClose: () => void
    currentUserId: number
    documentId: number
    documentName?: string
    backdrop?: boolean
}

// Type for room selection with only necessary fields
interface RoomSelection {
    id: number
    label: string // for react-select
    value: number
    name: string
    departmentName: string
    accessType: string
    urgent: boolean
}

export const DocumentShareModal: React.FC<DocumentShareModalProps> = observer(({
    isVisible = false,
    onClose,
    currentUserId,
    documentId,
    documentName,
    backdrop = true
}) => {
    const documentACLController = useService(DocumentACLsController, [])
    const currentUserDepartmentsController = useService(CurrentUserDepartmentsController, [])

    const { addToast } = useUI()

    const [isLoading, setIsLoading] = useState(false)
    const [selectedRooms, setSelectedRooms] = useState<Record<number, RoomSelection>>({})
    const [roomsWithConnectors, setRoomsWithConnectors] = useState<Set<number>>(new Set())

    useEffect(() => {
        const initControllers = async () => {
            if (!documentId || !currentUserId) return
            if (typeof documentId === 'object') return

            setIsLoading(true)
            try {
                await Promise.all([
                    documentACLController.init(documentId, currentUserId)
                ])
            } catch (error) {
                console.error('Failed to initialize controllers:', error)
            } finally {
                setIsLoading(false)
            }
        }

        initControllers()
    }, [currentUserId, documentId])

    const handleReadEditAccess = (room: RoomSelection) => {
        const currentAccessType = room.accessType
        const canShare = documentACLController.canShareDocument(currentAccessType)
        let newAccessType

        if (documentACLController.isEditor(currentAccessType)) {
            newAccessType = canShare ?
                DocumentACLsController.accessTypes.READ_AND_SHARE :
                DocumentACLsController.accessTypes.READ
        } else {
            newAccessType = canShare ?
                DocumentACLsController.accessTypes.EDIT_AND_SHARE :
                DocumentACLsController.accessTypes.EDIT
        }

        setSelectedRooms(prev => ({
            ...prev,
            [room.id]: { ...room, accessType: newAccessType }
        }))
    }

    const handleShareAccess = (room: RoomSelection) => {
        const currentAccessType = room.accessType
        let newAccessType

        switch (currentAccessType) {
            case DocumentACLsController.accessTypes.READ:
                newAccessType = DocumentACLsController.accessTypes.READ_AND_SHARE
                break
            case DocumentACLsController.accessTypes.READ_AND_SHARE:
                newAccessType = DocumentACLsController.accessTypes.READ
                break
            case DocumentACLsController.accessTypes.EDIT:
                newAccessType = DocumentACLsController.accessTypes.EDIT_AND_SHARE
                break
            case DocumentACLsController.accessTypes.EDIT_AND_SHARE:
                newAccessType = DocumentACLsController.accessTypes.EDIT
                break
            default:
                return
        }

        setSelectedRooms(prev => ({
            ...prev,
            [room.id]: { ...room, accessType: newAccessType }
        }))
    }

    const handleExistingACL = (access: string, acl: APIDataDocumentACL) => {
        if (!acl.id) return

        if (access === 'delete') {
            documentACLController.removeACL(acl.id)
        } else if (access === 'share') {
            documentACLController.toggleAccessTypeShare(acl.id)
        } else if (access === 'read' || access === 'edit') {
            documentACLController.toggleAccessTypeReadEdit(acl.id, access)
        }
    }

    const handleNotify = (room: RoomSelection, isUrgent: boolean) => {
        setSelectedRooms(prev => ({
            ...prev,
            [room.id]: { ...room, urgent: isUrgent }
        }))
    }

    const saveShareRoomAcl = async () => {
        const rooms = Object.values(selectedRooms)
        if (!rooms.length) return

        try {
            setIsLoading(true)
            await Promise.all(
                rooms.map(room =>
                    documentACLController.addACL(
                        room.id,
                        DocumentACLsController.principalTypes.Room,
                        room.accessType,
                        { urgent: room.urgent }
                    )
                )
            )
            setSelectedRooms({})
        } catch (error: any) {
            console.error('saveShareRoomAcl ERROR:', error)
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message
            })
        } finally {
            setTimeout(() => {
                onClose()
                setSelectedRooms({})
                setIsLoading(false)
            }, 1000)
        }
    }

    const checkRoomConnectors = async (roomId: number) => {
        try {
            const count = await documentACLController.getRoomConnectorCount(roomId)
            if (count > 0) {
                setRoomsWithConnectors(prev => new Set(prev).add(roomId))
            }
        } catch {
            // silently ignore — worst case we don't show the warning
        }
    }

    const handleAddRoom = (rooms: APIDataRoom[]) => {
        const newSelectedRooms = rooms.reduce((acc, room) => {
            // If room already exists in selectedRooms, keep its current state
            if (selectedRooms[room.id]) {
                acc[room.id] = selectedRooms[room.id]
                return acc
            }

            const roomDepartment = currentUserDepartmentsController.departments.find(dept => dept.id === room.departmentId)

            // Otherwise create a new room selection with default values
            acc[room.id] = {
                id: room.id,
                value: room.id,
                label: (room as any).label,
                name: room.name,
                departmentName: roomDepartment?.name,
                accessType: DocumentACLsController.accessTypes.READ,
                urgent: false
            }

            // Check if this newly added room has connectors
            checkRoomConnectors(room.id)

            return acc
        }, {} as Record<number, RoomSelection>)

        setSelectedRooms(newSelectedRooms)

        // Prune connector set to only keep IDs still in the selection
        const newSelectedIds = new Set(Object.keys(newSelectedRooms).map(Number))
        setRoomsWithConnectors(prev => {
            const pruned = new Set<number>()
            prev.forEach(id => {
                if (newSelectedIds.has(id)) pruned.add(id)
            })
            return pruned
        })
    }

    if (!documentId || !currentUserId) return null

    return (
        <Modal
            id="document-share-modal"
            visible={isVisible}
            alignment="center"
            backdrop={backdrop}
            onClose={onClose}
            onCloseDone={() => setSelectedRooms({})}
            size="lg">
            <ModalHeader
                title={documentName ? `Share "${documentName}"` : 'Share EverDoc'}
            />
            <ModalBody className="pb-5">
                <Row>
                    <Col xs={10} valign="top">
                        <RoomSearchSelect
                            key={documentACLController.aclRooms.length}
                            searchScope="member"
                            placeholder="Add rooms..."
                            onSelect={handleAddRoom}
                            selected={Object.values(selectedRooms)}
                            excluded={documentACLController.aclRooms?.map(acl => acl.principalId)}
                            formatOptionLabel={(room: RoomSelection, { context }: any) => {
                                if (context === 'value') {
                                    return room.name?.trim() || `RoomID:${room.id}`
                                }

                                return <RoomOptionLabel room={room} />
                            }}
                            className="mb-4"
                            tabSelectsValue={false}
                            isMulti
                        />
                    </Col>
                    <Col valign="top" align="right" className="ms-2">
                        <Button
                            loading={isLoading}
                            color="primary"
                            onClick={saveShareRoomAcl}>
                            Share
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={6} valign="start">
                        {!isLoading && Object.keys(selectedRooms).some(id => roomsWithConnectors.has(Number(id))) && (
                            <InfoBox color="warning" className="mb-3">
                                <Text>
                                    <strong>At least 1 room has external connectors enabled.</strong>
                                    <p>Your document may be sent to systems outside Evertel.  <a style={{color: 'currentColor', textDecoration: 'underline'}} href={Links.helpConnectors} target="_blank" rel="noopener noreferrer">
                                        Learn more
                                    </a></p>

                                </Text>
                            </InfoBox>
                        )}
                        {!isLoading && (
                            <div className="document-share-tile">
                                {Object.values(selectedRooms).map(selectedRoom => (
                                    <InfoBox
                                        color="info"
                                        className="fadeIn w-90"
                                        key={selectedRoom.id}
                                    >
                                        <Row>
                                            <Col>
                                                <Text bold className="mb-1">
                                                    {selectedRoom.label ?? selectedRoom.value}
                                                    {roomsWithConnectors.has(selectedRoom.id) && (
                                                        <Tooltip placement="top">
                                                            <TooltipTrigger asChild>
                                                                <span className="ms-2 text-info d-inline-flex align-items-center">
                                                                    <Icon name="share-nodes" color="info-text-emphasis" />
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                This room has external connectors
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </Text>
                                                {selectedRoom.departmentName &&
                                                        <Text color="muted" size="small" className="mb-2">
                                                            {selectedRoom.departmentName}
                                                        </Text>
                                                }
                                                <Checkbox
                                                    radio
                                                    id="notify"
                                                    checked={!selectedRoom.urgent}
                                                    onChange={() => handleNotify(selectedRoom, false)}
                                                    label="Notify room"
                                                />
                                                <Checkbox
                                                    radio
                                                    id="notify-urgent"
                                                    urgent
                                                    checked={selectedRoom.urgent}
                                                    onChange={() => handleNotify(selectedRoom, true)}
                                                    label="Notify room as urgent"
                                                />
                                            </Col>
                                            <>
                                                <Text bold className="mb-2">
                                                    Access:&nbsp;
                                                </Text>
                                                <ACLButton
                                                    attachId="document-share-modal"
                                                    acl={{ accessType: selectedRoom.accessType } as APIDataDocumentACL}
                                                    documentACLController={documentACLController}
                                                    onChangeACL={(access) => {
                                                        if (access === 'read' || access === 'edit') {
                                                            handleReadEditAccess(selectedRoom)
                                                        } else if (access === 'share') {
                                                            handleShareAccess(selectedRoom)
                                                        }
                                                    }}
                                                />
                                            </>
                                        </Row>
                                    </InfoBox>
                                ))}
                            </div>
                        )}
                    </Col>
                    <Col className="ms-4 overflow-hidden" valign="start">
                        <div className="mb-4">
                            <Text bold size={13}>
                                People with access
                            </Text>
                            <PerfectScrollbar options={{ suppressScrollX: true }}>
                                {documentACLController.aclUsers?.map(acl => (
                                    <ACL
                                        attachId="document-share-modal"
                                        key={acl.id}
                                        acl={acl}
                                        documentACLController={documentACLController}
                                        onChangeACL={handleExistingACL}
                                    />
                                ))}
                            </PerfectScrollbar>
                        </div>
                        <Text bold size={13}>
                            Rooms shared with
                        </Text>
                        <PerfectScrollbar
                            options={{ suppressScrollX: true }}
                            style={{ height: '30vh' }}>
                            {documentACLController.aclRooms?.map(acl => (
                                <ACL
                                    attachId="document-share-modal"
                                    key={acl.id}
                                    acl={acl}
                                    documentACLController={documentACLController}
                                    onChangeACL={handleExistingACL}
                                />
                            ))}
                        </PerfectScrollbar>
                        {!documentACLController.aclRooms?.length && (
                            <Text tag="p" size={12} className="mt-2">
                                <i>Not currently shared with any rooms</i>
                            </Text>
                        )}
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
})

const RoomOptionLabel: React.FC<{ room: APIDataRoom }> = observer(({ room }) => {
    const departmentController = useService(DepartmentController, [room.departmentId])

    useEffect(() => {
        if (room.departmentId) {
            departmentController.init(room.departmentId)
        }
    }, [room.id])

    if (!room) return null

    const roomName = room.name?.trim() ? room.name : `RoomID:${room.id}`
    const department = departmentController?.department

    return (
        <div className="cursor-pointer">
            <Row>
                <Text bold>
                    # {roomName}
                </Text>
            </Row>
            {department && (
                <Row valign="center" className="mt-1">
                    <DepartmentAvatar
                        departmentId={department.id}
                        width={15}
                        height={15}
                        showGuestBadge={false}
                    />
                    <Text
                        color="muted"
                        size="small"
                        className="ms-1">
                        {departmentController.department.name}
                    </Text>
                </Row>
            )}
        </div>
    )
})
